import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import {
  createMailFolderMutation,
  deleteMailFolderMutation,
  type MailComposeKind,
  mailFoldersQuery,
  moveMailMessageMutation,
  saveMailDraftMutation,
  type SendMailInput,
  sendMailMutation,
  sendSavedMailDraftMutation,
  updateMailFolderMutation,
  updateMailMessageStateMutation,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { Subject } from 'rxjs';

import {
  OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import type {
  MailCreateFolderMutationPayload,
  MailDeleteFolderMutationPayload,
  MailMoveMessagesMutationPayload,
  MailSendMutationPayload,
  MailUpdateFolderMutationPayload,
  MailUpdateMessageStateMutationPayload,
  OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';

type MailMutationOperation =
  | 'mail.updateMessageState'
  | 'mail.moveMessages'
  | 'mail.createFolder'
  | 'mail.updateFolder'
  | 'mail.deleteFolder'
  | 'mail.send';

type MailMutationRecord = OfflineMutationRecord<MailMutationOperation>;
/** A send queued while offline, tied to the draft the compose saved. */
export type QueuedSendMailInput = SendMailInput & {
  draftMessageId?: string | null;
};
type ReplayableSendMailInput = QueuedSendMailInput & {
  idempotencyKey?: string | null;
};

export class MailMutationProviderTimeoutError extends Error {
  readonly code = 'MAIL_PROVIDER_CONFIRMATION_TIMEOUT';

  constructor() {
    super('Mail provider confirmation timed out');
    this.name = 'MailMutationProviderTimeoutError';
  }
}

export class MailMutationOutboxService extends Service {
  readonly completed$ = new Subject<MailMutationRecord>();
  readonly failed$ = new Subject<MailMutationRecord>();
  readonly pendingMutations$ = this.outbox.pendingMutations$;
  readonly failedMutations$ = this.outbox.failedMutations$;

  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly outbox: OfflineMutationOutboxService
  ) {
    super();
    this.registerExecutors();
    const failedSubscription = this.outbox.failed$.subscribe(mutation => {
      if (isMailMutation(mutation)) {
        this.failed$.next(mutation);
      }
    });
    const completedSubscription = this.outbox.completed$.subscribe(mutation => {
      if (isMailMutation(mutation)) {
        this.completed$.next(mutation);
      }
    });
    this.disposables.push(() => failedSubscription.unsubscribe());
    this.disposables.push(() => completedSubscription.unsubscribe());
  }

  enqueueUpdateMessageState(
    input: MailUpdateMessageStateMutationPayload['input']
  ) {
    return this.outbox.enqueue({
      operation: 'mail.updateMessageState',
      payload: { input },
      idempotencyKey: [
        'mail.updateMessageState',
        input.workspaceId,
        input.messageId,
        input.isRead ?? '-',
        input.isFlagged ?? '-',
        input.isStarred ?? '-',
      ].join(':'),
      dedupePending: true,
    });
  }

  enqueueMoveMessages(
    workspaceId: string,
    messageIds: string[],
    folderId: string
  ) {
    const uniqueMessageIds = Array.from(new Set(messageIds)).filter(Boolean);
    return this.outbox.enqueue({
      operation: 'mail.moveMessages',
      payload: { workspaceId, messageIds: uniqueMessageIds, folderId },
      idempotencyKey: [
        'mail.moveMessages',
        workspaceId,
        folderId,
        ...uniqueMessageIds,
      ].join(':'),
      dedupePending: true,
    });
  }

  enqueueCreateFolder(
    input: MailCreateFolderMutationPayload['input'],
    id?: string
  ) {
    return this.outbox.enqueue({
      id,
      operation: 'mail.createFolder',
      payload: { input },
      idempotencyKey: [
        'mail.createFolder',
        input.workspaceId,
        input.accountId,
        input.parentFolderId ?? input.parentPath ?? '-',
        input.path ?? input.displayName,
      ].join(':'),
    });
  }

  enqueueUpdateFolder(input: MailUpdateFolderMutationPayload['input']) {
    return this.enqueueUpdateFolderRecord(input);
  }

  async enqueueUpdateFolderAndWait(
    input: MailUpdateFolderMutationPayload['input'],
    timeoutMs: number
  ): Promise<void> {
    const mutationId = nanoid();
    const completion = this.waitForMutation(mutationId, timeoutMs);
    try {
      await Promise.all([
        this.enqueueUpdateFolderRecord(input, mutationId),
        completion.promise,
      ]);
    } catch (error) {
      completion.cancel();
      throw error;
    }
  }

  private enqueueUpdateFolderRecord(
    input: MailUpdateFolderMutationPayload['input'],
    id?: string
  ) {
    return this.outbox.enqueue({
      id,
      operation: 'mail.updateFolder',
      payload: { input },
      idempotencyKey: [
        'mail.updateFolder',
        input.workspaceId,
        input.folderId,
        input.displayName ?? '-',
      ].join(':'),
    });
  }

  private waitForMutation(mutationId: string, timeoutMs: number) {
    let settled = false;
    let completedSubscription: { unsubscribe(): void } | null = null;
    let failedSubscription: { unsubscribe(): void } | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      completedSubscription?.unsubscribe();
      failedSubscription?.unsubscribe();
      completedSubscription = null;
      failedSubscription = null;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const cancel = () => {
      if (settled) return;
      settled = true;
      cleanup();
    };
    const promise = new Promise<void>((resolve, reject) => {
      completedSubscription = this.completed$.subscribe(mutation => {
        if (mutation.id !== mutationId || settled) return;
        settled = true;
        cleanup();
        resolve();
      });
      failedSubscription = this.failed$.subscribe(mutation => {
        if (mutation.id !== mutationId || settled) return;
        settled = true;
        cleanup();
        reject(new Error(mutation.lastError || 'Mail folder update failed'));
      });
      timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new MailMutationProviderTimeoutError());
      }, timeoutMs);
    });

    return { cancel, promise };
  }

  enqueueDeleteFolder(workspaceId: string, folderId: string) {
    return this.outbox.enqueue({
      operation: 'mail.deleteFolder',
      payload: { workspaceId, folderId },
      idempotencyKey: ['mail.deleteFolder', workspaceId, folderId].join(':'),
      dedupePending: true,
    });
  }

  enqueueSend(input: ReplayableSendMailInput) {
    const idempotencyKey =
      input.idempotencyKey ??
      ['mail.send', input.workspaceId, input.accountId, nanoid()].join(':');
    return this.outbox.enqueue({
      operation: 'mail.send',
      payload: { input: { ...input, idempotencyKey } },
      idempotencyKey,
    });
  }

  retryFailed(id: string) {
    return this.outbox.retryFailed(id);
  }

  removeFailed(id: string) {
    return this.outbox.removeFailed(id);
  }

  private registerExecutors(): void {
    this.disposables.push(
      this.outbox.registerExecutor(
        'mail.updateMessageState',
        async mutation => {
          const payload =
            mutation.payload as MailUpdateMessageStateMutationPayload;
          await this.requireServer().gql({
            query: updateMailMessageStateMutation,
            variables: { input: payload.input },
          });
        }
      ),
      this.outbox.registerExecutor('mail.moveMessages', async mutation => {
        const payload = mutation.payload as MailMoveMessagesMutationPayload;
        for (const messageId of payload.messageIds) {
          await this.requireServer().gql({
            query: moveMailMessageMutation,
            variables: {
              input: {
                workspaceId: payload.workspaceId,
                messageId,
                folderId: payload.folderId,
              },
            },
          });
        }
      }),
      this.outbox.registerExecutor('mail.createFolder', async mutation => {
        const payload = mutation.payload as MailCreateFolderMutationPayload;
        try {
          await this.requireServer().gql({
            query: createMailFolderMutation,
            variables: { input: payload.input },
          });
        } catch (error) {
          if (!(await this.isFolderAlreadyVisible(payload, error))) {
            throw error;
          }
        }
      }),
      this.outbox.registerExecutor('mail.updateFolder', async mutation => {
        const payload = mutation.payload as MailUpdateFolderMutationPayload;
        await this.requireServer().gql({
          query: updateMailFolderMutation,
          variables: { input: payload.input },
        });
      }),
      this.outbox.registerExecutor('mail.deleteFolder', async mutation => {
        const payload = mutation.payload as MailDeleteFolderMutationPayload;
        await this.requireServer().gql({
          query: deleteMailFolderMutation,
          variables: {
            workspaceId: payload.workspaceId,
            folderId: payload.folderId,
          },
        });
      }),
      this.outbox.registerExecutor('mail.send', async mutation => {
        const { draftMessageId, ...input } = (
          mutation.payload as MailSendMutationPayload
        ).input;
        if (!draftMessageId) {
          await this.requireServer().gql({
            query: sendMailMutation,
            variables: { input: input as SendMailInput },
          });
          return;
        }
        // The compose saved this draft before going offline: fill it with
        // what was written and send it, so no empty draft stays behind.
        await this.requireServer().gql({
          query: saveMailDraftMutation,
          variables: {
            input: {
              workspaceId: input.workspaceId,
              accountId: input.accountId,
              draftMessageId,
              composeKind: input.composeKind as MailComposeKind | null,
              sourceMessageId: input.sourceMessageId ?? null,
              inReplyToHeader: input.inReplyToHeader ?? null,
              referencesHeader: input.referencesHeader ?? null,
              to: input.to,
              cc: input.cc ?? [],
              bcc: input.bcc ?? [],
              subject: input.subject ?? null,
              bodyText: input.bodyText ?? null,
              bodyHtml: input.bodyHtml ?? null,
              attachments: [],
            },
          },
        });
        await this.requireServer().gql({
          query: sendSavedMailDraftMutation,
          variables: {
            input: { workspaceId: input.workspaceId, draftMessageId },
          },
        });
      })
    );
  }

  private requireServer() {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new OfflineMutationTransientError('No server');
    }
    return server;
  }

  private async isFolderAlreadyVisible(
    payload: MailCreateFolderMutationPayload,
    error: unknown
  ): Promise<boolean> {
    if (!isAlreadyExistsError(error)) {
      return false;
    }
    const response = await this.requireServer().gql({
      query: mailFoldersQuery,
      variables: {
        workspaceId: payload.input.workspaceId,
        accountId: payload.input.accountId,
      },
    });
    return response.mailFolders.some(folder => {
      if (payload.input.path) {
        return folder.path === payload.input.path;
      }
      return (
        folder.displayName === payload.input.displayName &&
        folder.accountId === payload.input.accountId
      );
    });
  }
}

function isMailMutation(
  mutation: OfflineMutationRecord
): mutation is MailMutationRecord {
  return mutation.operation.startsWith('mail.');
}

function isAlreadyExistsError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return /already exists|duplicate|unique/i.test(message);
}
